/**
 * persistenceClient — typed fetch wrappers for /api/persist/* endpoints.
 * Used by useDbSync to load and save terminal state.
 */

const BASE = '/api/persist';

// ─── Types (mirrors API db/types.ts) ─────────────────────────────────────────

export interface DbOrder {
  id:           string;
  symbol:       string;
  side:         string;
  type:         string;
  size:         number;
  leverage:     number;
  status:       string;
  fill_price:   number | null;
  exchange:     string;
  mode:         string;
  pnl:          number | null;
  timestamp:    number;
  closed_at:    number | null;
  close_reason: string | null;
}

export interface DbPriceAlert {
  id:           string;
  symbol:       string;
  condition:    string;
  target_price: number;
  created_at:   number;
  fired_at:     number | null;
  repeat:       number;
  note:         string | null;
}

export interface DbEquityPoint {
  timestamp: number;
  equity:    number;
}

export interface DbSnapshot {
  settings:    Record<string, string>;  // includes JSON blobs for complex fields
  orders:      DbOrder[];
  priceAlerts: DbPriceAlert[];
  equityCurve: DbEquityPoint[];
}

// ─── Client functions ─────────────────────────────────────────────────────────

/** Load the full snapshot on startup */
export async function loadSnapshot(): Promise<DbSnapshot> {
  const res = await fetch(`${BASE}/load`);
  if (!res.ok) throw new Error(`persist/load failed: ${res.status}`);
  return res.json() as Promise<DbSnapshot>;
}

/** Upsert key-value settings */
export async function saveSettings(kv: Record<string, string>): Promise<void> {
  await fetch(`${BASE}/settings`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(kv),
  });
}

/** Upsert orders */
export async function saveOrders(orders: DbOrder[]): Promise<void> {
  if (orders.length === 0) return;
  await fetch(`${BASE}/orders`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(orders),
  });
}

/** Upsert price alerts */
export async function saveAlerts(alerts: DbPriceAlert[]): Promise<void> {
  if (alerts.length === 0) return;
  await fetch(`${BASE}/price-alerts`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(alerts),
  });
}

/** Delete a single alert */
export async function deleteAlert(id: string): Promise<void> {
  await fetch(`${BASE}/price-alerts/${id}`, { method: 'DELETE' });
}

/** Append equity curve points */
export async function saveEquityPoints(points: DbEquityPoint[]): Promise<void> {
  if (points.length === 0) return;
  await fetch(`${BASE}/equity`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(points),
  });
}

/** Check which backend is active */
export async function getPersistStatus(): Promise<{ backend: 'sqlite' | 'supabase'; ok: boolean }> {
  const res = await fetch(`${BASE}/status`);
  return res.json() as Promise<{ backend: 'sqlite' | 'supabase'; ok: boolean }>;
}
